import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useHistory } from "@/hooks/useHistory";
import { getMediaById } from "@/utils/media";

function formatViewedAt(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) return "刚刚浏览";
  if (diff < hour) return `${Math.floor(diff / minute)} 分钟前浏览`;
  if (diff < day) return `${Math.floor(diff / hour)} 小时前浏览`;
  return `${Math.floor(diff / day)} 天前浏览`;
}

export default function HistoryPage() {
  usePageMeta({
    title: "浏览记录 - 17C.COM",
    description: "查看你在 17C.COM 近期浏览过的影视作品。浏览记录保存在浏览器本地，方便快速找回之前看过的内容。",
    keywords: "浏览记录,观看历史,17C.COM",
  });

  const { history, clearHistory } = useHistory();

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "浏览记录" }]} />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            浏览记录
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            最近浏览 <span className="font-bold text-primary-600">{history.length}</span> 条记录
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/favorites"
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-heart-3-line" aria-hidden="true" />
            我的收藏
          </Link>
          {history.length > 0 && (
            <button
              type="button"
              onClick={clearHistory}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-600 transition-colors hover:bg-background-100"
            >
              <i className="ri-delete-bin-line" aria-hidden="true" />
              清空记录
            </button>
          )}
        </div>
      </div>

      {history.length > 0 ? (
        <div className="space-y-3">
          {history.map((entry) => {
            const item = getMediaById(entry.id);
            if (!item) return null;
            return (
              <div
                key={entry.id}
                className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3"
              >
                <Link to={`/detail/${item.id}`} className="shrink-0">
                  <div className="h-24 w-[72px] overflow-hidden rounded-md bg-background-200">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={item.title}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                </Link>
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/detail/${item.id}`}
                    className="line-clamp-1 font-heading text-base font-bold text-foreground-950 transition-colors hover:text-primary-600"
                  >
                    {item.title}
                  </Link>
                  <p className="mt-1 line-clamp-1 text-sm text-foreground-500">
                    {item.year} · {item.region} · {item.genres.join("/")}
                  </p>
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-foreground-400">
                    <i className="ri-time-line" aria-hidden="true" />
                    {formatViewedAt(entry.viewedAt)}
                  </p>
                </div>
                <Link
                  to={`/play/${item.id}`}
                  className="hidden shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 sm:flex"
                >
                  <i className="ri-play-fill" aria-hidden="true" />
                  播放
                </Link>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon="ri-history-line"
          title="暂无浏览记录"
          description="浏览影视详情后，记录会自动出现在这里，方便你快速找回。"
          action={
            <Link
              to="/category"
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-play-list-line" aria-hidden="true" />
              去浏览作品
            </Link>
          }
        />
      )}
    </div>
  );
}